/**
 * The number on the home-screen icon.
 *
 * WHAT IT COUNTS, and it is deliberately much less than the app's own nav badges count.
 *
 * Tim's rule, 26 August: a deadline that is incomplete AND within a week of falling due, plus —
 * for an admin — an access request waiting to be answered. Nothing else. The nav badges inside
 * the app can afford to say "you have not done this yet" the moment a thing opens, because you
 * are already looking at the app and a dot beside a tab costs nothing. A number on the home
 * screen is different: it sits on somebody's phone between a text message and a missed call, and
 * a 1 that has been there since March is a 1 nobody reads by August.
 *
 * So this is the same "you owe this" the nav computes — one definition, two surfaces, and the nav
 * is the one that already knows about phases and closed gates — narrowed to the week before it is
 * actually due.
 *
 * WHEN IT UPDATES, which is the part worth being straight about. `setAppBadge` can only be called
 * by the page or by its service worker. The page runs when the app is open; on iOS the service
 * worker runs on a `push` event and essentially nothing else — `periodicSync` is Chromium-only.
 * So without a push server this number is set when a GM opens the app and then PERSISTS on the
 * icon after he closes it. It is a reminder of what he saw, not a live counter, and a deadline
 * crossing into its last week while the app is shut will not light it up until he next opens it.
 *
 * That is worth having anyway — the count survives on the icon, which is more than the nav badges
 * do — but it is not the thing a push notification would be, and nothing here should be described
 * as though it is.
 */

import { type Urgency, urgencyOf } from './deadlines.js';

/** Deadlines that can carry a badge, and the date each one is measured against. */
export interface BadgeInput {
  /**
   * The nav's own badges, by section — `{ keepers: 1 }`. Already gated on the phase, on the
   * deadline still being open, and on this GM not having done the thing.
   */
  readonly badges: Readonly<Record<string, number>>;
  /** The deadline behind each section, so lateness can be measured. Missing means "no date". */
  readonly dueBySection: Readonly<Record<string, string | null | undefined>>;
  /** Access requests waiting on an answer. Counted only for somebody who can answer them. */
  readonly accessRequests?: number;
  readonly canAdmit?: boolean;
  /**
   * A PROXY THIS GM STILL OWES, and an open 24-hour ballot he has not answered.
   *
   * Neither is a deadline in the `dueBySection` sense — the first has no date on it beyond the
   * meeting, and the second is a clock that started in a room he was not in. Both are the app
   * asking a specific person for something specific, which is exactly what the icon is for, so
   * they are counted whatever the week-before rule says about the rest.
   */
  readonly proxyOwed?: boolean;
  readonly ballotsOpen?: number;
  readonly now?: number;
}

/** The urgencies that count as "within a week of its due date". */
const SOON: ReadonlySet<Urgency> = new Set<Urgency>(['week', 'day']);

/**
 * Which sections are close enough to their deadline to be worth a number on somebody's phone.
 *
 * Returned as well as counted because the caller has to be able to say WHY the badge is what it
 * is — a number with no explanation anywhere in the app is the other way badges get ignored.
 */
export function badgedSections(input: BadgeInput): string[] {
  const now = input.now ?? Date.now();
  return Object.keys(input.badges)
    .filter((section) => (input.badges[section] ?? 0) > 0)
    .filter((section) => SOON.has(urgencyOf(input.dueBySection[section], now)))
    .sort();
}

/**
 * The number to put on the icon. Zero means clear it.
 *
 * Sections rather than the nav's counts: the nav can say "3" for three unread things inside one
 * screen, and on the icon what a GM needs to know is how many separate things are waiting, not
 * how many rows they add up to.
 */
export function appBadgeCount(input: BadgeInput): number {
  const deadlines = badgedSections(input).length;
  const requests = input.canAdmit ? Math.max(0, input.accessRequests ?? 0) : 0;
  const proxy = input.proxyOwed ? 1 : 0;
  const ballots = Math.max(0, input.ballotsOpen ?? 0);
  return deadlines + requests + proxy + ballots;
}

/**
 * Put it on the icon, or take it off.
 *
 * Every call is guarded: the API is iOS 16.4+ and only in an installed app, it rejects when the
 * page is not allowed to badge, and there is no version of this worth throwing an error over. A
 * league manager whose phone is a year out of date should get an app that works and no badge.
 */
export async function showAppBadge(count: number): Promise<void> {
  const nav = typeof navigator === 'undefined' ? null : navigator as Navigator & {
    setAppBadge?: (n?: number) => Promise<void>;
    clearAppBadge?: () => Promise<void>;
  };
  if (!nav?.setAppBadge) return;
  try {
    if (count > 0) await nav.setAppBadge(count);
    else await nav.clearAppBadge?.();
  } catch { /* Denied, unsupported, or not installed. None of those is worth a broken screen. */ }
}
